import { useState } from "react";
import { supabase } from "../lib/supabase";
import { P } from "../styles/theme";

const CANCEL_POLICY_HOURS = 48;

export default function FindMyBooking({ onBack }) {
  const [reference,  setReference]  = useState('');
  const [email,      setEmail]      = useState('');
  const [booking,    setBooking]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelDone, setCancelDone] = useState(null); // { refund_status, within_policy }

  const s = {
    container: {
      maxWidth: '450px', margin: '0 auto', background: P.bg, minHeight: '100vh',
      padding: '20px 16px 40px', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: P.text,
    },
    card: {
      background: P.card, borderRadius: '16px', padding: '20px',
      border: `1px solid ${P.border}`, boxShadow: '0 4px 14px rgba(139,94,87,0.04)',
      marginBottom: '16px',
    },
    input: {
      width: '100%', padding: '13px', borderRadius: '8px',
      border: `1.5px solid ${P.border}`, outline: 'none',
      boxSizing: 'border-box', fontSize: '16px',
      fontFamily: 'inherit',
    },
    label: {
      fontSize: '11px', fontWeight: 700, color: P.muted,
      textTransform: 'uppercase', letterSpacing: '0.05em',
      display: 'block', marginBottom: '8px',
    },
    btn: (color = P.rose) => ({
      width: '100%', padding: '15px', borderRadius: '12px', border: 'none',
      background: `linear-gradient(135deg, ${color}, ${P.deep})`,
      color: '#fff', fontSize: '16px', fontWeight: 700, cursor: 'pointer', minHeight: '50px',
    }),
    backBtn: {
      background: 'none', border: `1px solid ${P.border}`, borderRadius: '8px',
      padding: '8px 16px', cursor: 'pointer', color: P.muted, fontSize: '14px', minHeight: '44px',
    },
  };

  const handleFind = async () => {
    const ref = reference.trim().toUpperCase();
    const em  = email.trim().toLowerCase();
    if (!ref || !em) { setError('Please enter both your booking reference and email.'); return; }

    setLoading(true); setError(''); setBooking(null); setCancelDone(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('guest-booking-lookup', {
        body: { reference: ref, email: em, action: 'lookup' },
      });
      if (fnErr) throw new Error(fnErr.message);
      if (data.error) { setError(data.error); }
      else { setBooking(data.booking); }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleCancel = async () => {
    const hrs = (new Date(booking.booking_date).getTime() - Date.now()) / 3_600_000;
    const msg = hrs <= CANCEL_POLICY_HOURS
      ? `Cancellations within ${CANCEL_POLICY_HOURS} hours forfeit the deposit per our policy.\n\nCancel anyway?`
      : 'Your deposit will be refunded within 5–10 business days. Confirm cancellation?';
    if (!window.confirm(msg)) return;

    setCancelling(true); setError('');
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('guest-booking-lookup', {
        body: {
          reference: reference.trim().toUpperCase(),
          email:     email.trim().toLowerCase(),
          action:    'cancel',
        },
      });
      if (fnErr) throw new Error(fnErr.message);
      if (data.error) { setError(data.error); }
      else {
        setBooking(prev => ({ ...prev, status: 'cancelled' }));
        setCancelDone({ refund_status: data.refund_status, within_policy: data.within_policy });
      }
    } catch (err) {
      setError(err.message || 'Could not cancel booking. Please contact us directly.');
    }
    setCancelling(false);
  };

  const canCancel = booking && booking.status !== 'cancelled' &&
    booking.status !== 'completed' && booking.status !== 'no_show';

  const statusColor = {
    pending:   P.amber,
    confirmed: '#6EA882',
    completed: P.deep,
    cancelled: P.red,
    no_show:   '#B56060',
  };

  const refundMessage = (rs, withinPolicy) => {
    if (rs === 'refunded')      return '✅ Your deposit will be refunded within 5–10 business days.';
    if (rs === 'forfeited')     return `⚠️ Per our ${CANCEL_POLICY_HOURS}-hour policy, the deposit has been forfeited.`;
    if (rs === 'refund_failed') return '⚠️ Refund could not be processed automatically — please contact us.';
    return 'Booking cancelled.';
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
        <button style={s.backBtn} onClick={onBack}>← Back</button>
        <div>
          <h2 style={{ color: P.deep, fontWeight: 600, fontSize: '22px', margin: 0 }}>Find My Booking</h2>
          <p style={{ fontSize: '12px', color: P.muted, marginTop: '2px' }}>
            Enter your booking reference and email
          </p>
        </div>
      </div>

      {/* Search form */}
      <div style={s.card}>
        <div style={{ marginBottom: '14px' }}>
          <label style={s.label}>Booking Reference</label>
          <input
            style={{ ...s.input, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, fontSize: '18px' }}
            placeholder="NBA-XXXX-XXXX"
            value={reference}
            onChange={e => setReference(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleFind()}
            maxLength={12}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={s.label}>Email Address</label>
          <input
            type="email"
            style={s.input}
            placeholder="your@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFind()}
          />
        </div>
        {error && (
          <p style={{ fontSize: '13px', color: P.red, fontWeight: 600, marginBottom: '12px' }}>
            ⚠️ {error}
          </p>
        )}
        <button onClick={handleFind} disabled={loading} style={s.btn()}>
          {loading ? 'Searching...' : 'Find Booking'}
        </button>
      </div>

      {/* Cancellation result */}
      {cancelDone && (
        <div style={{ ...s.card, background: '#F0FFF5', border: `1px solid #6EA88260` }}>
          <p style={{ fontWeight: 700, color: '#6EA882', marginBottom: '6px' }}>Booking Cancelled</p>
          <p style={{ fontSize: '14px', color: P.text }}>{refundMessage(cancelDone.refund_status, cancelDone.within_policy)}</p>
        </div>
      )}

      {/* Booking result */}
      {booking && (
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <p style={{ fontWeight: 800, fontSize: '16px', color: P.deep, margin: '0 0 2px' }}>
                {booking.client_name}
              </p>
              <p style={{ fontSize: '12px', color: P.muted, margin: 0, fontFamily: 'monospace', fontWeight: 700 }}>
                {booking.booking_reference}
              </p>
            </div>
            <span style={{
              background: (statusColor[booking.status] || P.muted) + '20',
              color: statusColor[booking.status] || P.muted,
              padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
            }}>
              {booking.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          <div style={{ background: P.bg, borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
            <p style={{ fontWeight: 700, color: P.deep, fontSize: '15px', margin: '0 0 4px' }}>
              {new Date(booking.booking_date).toLocaleString('en-AU', {
                weekday: 'long', year: 'numeric', month: 'long',
                day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </p>
            {booking.service_name && (
              <p style={{ fontSize: '14px', color: P.text, margin: '4px 0' }}>{booking.service_name}</p>
            )}
            <p style={{ fontSize: '13px', color: P.muted, margin: '6px 0 0' }}>
              Deposit paid: <strong style={{ color: P.deep }}>${booking.deposit_amount}</strong>
              {Number(booking.balance_due) > 0 && (
                <span> · Balance due: <strong style={{ color: P.rose }}>${booking.balance_due}</strong></span>
              )}
            </p>
          </div>

          {canCancel && !cancelDone && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              style={{
                width: '100%', padding: '14px', borderRadius: '12px',
                border: `1.5px solid ${P.red}60`, background: '#FFF5F5',
                color: P.red, fontSize: '15px', fontWeight: 700,
                cursor: cancelling ? 'not-allowed' : 'pointer', minHeight: '50px',
              }}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </button>
          )}

          {booking.status === 'cancelled' && !cancelDone && (
            <p style={{ fontSize: '13px', color: P.muted, textAlign: 'center' }}>
              This booking has been cancelled.
            </p>
          )}
        </div>
      )}

      <p style={{ textAlign: 'center', fontSize: '12px', color: P.muted, marginTop: '8px' }}>
        Can't find your booking?{' '}
        <a href="https://www.instagram.com/nails.by.aurora.melb" target="_blank" rel="noopener noreferrer"
          style={{ color: P.rose, textDecoration: 'none', fontWeight: 600 }}>
          Contact us on Instagram
        </a>
      </p>
    </div>
  );
}
